import React, { useState } from 'react';
import { ArrowRight, ArrowLeft, Calendar, Clock, MapPin, Users, Check, CreditCard, FileText, ChevronDown, AlertTriangle, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageTransition } from '../../components/layout/PageTransition';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input, TextArea } from '../../components/ui/Input';
import { DecorativeShape } from '../../components/DecorativeShape';
import { Animate } from '../../components/ui/Animate';
import { useLanguage } from '../../context/LanguageContext';

/* ------------------------------------------------------------------
   STILLINGAR - allt sem þarf að fylla út áður en síðan fer í loftið
   ------------------------------------------------------------------
   - Greiðslutenglar (Teya, "Rennur aldrei út"): einn fyrir hvert verð
     (fullt verð og snemmskráningarverð). Tómur tengill = kortagreiðsla falin,
     aðeins reikningur.
   - enabled: false felur dagsetningu (t.d. ef hún fyllist eða fellur niður).
   - earlyUntil: síðasti dagur snemmskráningarverðs (YYYY-MM-DD).
   ------------------------------------------------------------------ */
const CONFIG = {
  // true = gulur "DRÖG" borði efst á síðunni.
  draft: false,
  formEndpoint: 'https://formsubmit.co/ajax/helgi@helgig.com',
  courseName: 'Betri samskipti og heilbrigðari ágreiningar',
  location: 'Reykjavík - staðsetning kynnt við skráningu',
  maxParticipants: 14,
  minParticipants: 6,
  cancellationPolicy: 'Full endurgreiðsla ef afbókað er minnst 7 dögum fyrir námskeiðið. Eftir það er velkomið að senda annan þátttakanda í þinn stað. Ef lágmarksfjöldi (6 þátttakendur) næst ekki fellur námskeiðið niður og þú færð fulla endurgreiðslu, eða færir þig á næstu dagsetningu.',
  options: [{
    id: 'half-2026-11-12',
    enabled: false,
    format: 'half' as const,
    name: 'Hálfur dagur',
    date: 'Fimmtudagur 12. nóvember 2026',
    time: '9:00 - 12:30',
    price: 39900,
    earlyPrice: 34900,
    earlyUntil: '2026-11-03',
    paymentLink: '',
    paymentLinkEarly: ''
  }, {
    id: 'half-2026-11-25',
    enabled: true,
    format: 'half' as const,
    name: 'Hálfur dagur',
    date: 'Miðvikudagur 25. nóvember 2026',
    time: '9:00 - 12:30',
    price: 39900,
    earlyPrice: 34900,
    earlyUntil: '2026-11-11',
    // Teya greiðslutenglar - fullt verð og snemmskráningarverð
    paymentLink: '',
    paymentLinkEarly: ''
  }, {
    id: 'half-2026-12-09',
    enabled: true,
    format: 'half' as const,
    name: 'Hálfur dagur',
    date: 'Miðvikudagur 9. desember 2026',
    time: '9:00 - 12:30',
    price: 39900,
    earlyPrice: 34900,
    earlyUntil: '2026-11-25',
    paymentLink: '',
    paymentLinkEarly: ''
  }, {
    id: 'full-2027-01-20',
    enabled: true,
    format: 'full' as const,
    name: 'Heill dagur',
    date: 'Miðvikudagur 20. janúar 2027',
    time: '9:00 - 16:00',
    price: 69900,
    earlyPrice: 59900,
    earlyUntil: '2027-01-06',
    paymentLink: '',
    paymentLinkEarly: ''
  }]
};
type CourseOption = (typeof CONFIG.options)[number];
type PaymentMethod = 'card' | 'invoice';
function formatISK(n: number) {
  return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ' kr.';
}
const MONTHS = ['jan.', 'feb.', 'mars', 'apríl', 'maí', 'júní', 'júlí', 'ágúst', 'sept.', 'okt.', 'nóv.', 'des.'];
function shortDate(iso: string) {
  const [, m, d] = iso.split('-').map(Number);
  return `${d}. ${MONTHS[m - 1]}`;
}
function todayISO() {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
}
function isEarly(o: CourseOption) {
  return !!o.earlyUntil && todayISO() <= o.earlyUntil;
}
function currentPrice(o: CourseOption) {
  return isEarly(o) ? o.earlyPrice : o.price;
}
function currentLink(o: CourseOption) {
  return isEarly(o) ? o.paymentLinkEarly : o.paymentLink;
}
const recognise = ['Samstarfsmaður sem þú forðast á fundum, og þú veist ekki hvort þetta er ágreiningur eða bara ólíkur stíll.', 'Starfsmaður sem upplifir yfirgang, á meðan hinn aðilinn telur sig einfaldlega vera ákveðinn.', 'Teymi þar sem allir vita af núningnum en enginn nefnir hann, og það er þitt að gera eitthvað.', 'Samtal sem þú hefur frestað vikum saman af því að þú veist ekki hvernig á að byrja það.'];
const outcomes = ['Þú greinir hvort þú stendur frammi fyrir ágreiningi eða ólíkum samskiptastíl, og bregst við í samræmi við það.', 'Þú þekkir eigin viðbrögð betur og heldur ró þinni þegar eitthvað kveikir í þér.', 'Þú metur hversu langt málið er gengið (umferðarljósin) og veist hvenær þarf utanaðkomandi aðstoð.', 'Þú tekur frumkvæðið og finnur næsta rétta skref, skref sem býður hinum aðilanum að taka þátt.'];
const audience = ['Stjórnendur og teymisstjórar', 'Mannauðsfólk sem styður teymi og stjórnendur', 'Verkefnastjórar og aðrir sem leiða án formlegs valds', 'Allir sem vilja takast á við erfið samskipti af meira öryggi'];
const halfDayAgenda = [{
  t: '9:00',
  d: 'Móttaka og kynning - hvaða aðstæður komu með þér?'
}, {
  t: '9:20',
  d: 'Samskiptaskalinn: hvað er ágreiningur og hvað ekki'
}, {
  t: '9:55',
  d: 'Þín viðbrögð: mynstur, kveikjur og sjálfsstjórn (æfing)'
}, {
  t: '10:30',
  d: 'Hlé'
}, {
  t: '10:45',
  d: 'Umferðarljósin: hvernig ágreiningar stigmagnast'
}, {
  t: '11:20',
  d: 'Verkfærakistan: aðferðir æfðar í pörum'
}, {
  t: '12:10',
  d: 'Samantekt: eitt skref sem þú prófar í næstu viku'
}];
const fullDayAgenda = [{
  t: '9:00',
  d: 'Móttaka og kynning - hvaða aðstæður komu með þér?'
}, {
  t: '9:20',
  d: 'Samskiptaskalinn og ólíkar tegundir ágreininga'
}, {
  t: '10:15',
  d: 'Hlé'
}, {
  t: '10:30',
  d: 'Þín viðbrögð: mynstur, kveikjur og sjálfsstjórn (æfingar)'
}, {
  t: '12:00',
  d: 'Hádegismatur'
}, {
  t: '12:45',
  d: 'Stigmögnun: umferðarljósin og líkan Glasl'
}, {
  t: '13:45',
  d: 'Hlé'
}, {
  t: '14:00',
  d: 'Verkfærakistan: æfingar á raunverulegum aðstæðum þátttakenda'
}, {
  t: '15:30',
  d: 'Samantekt og næstu skref'
}];
const funding = [{
  t: 'Stéttarfélög',
  d: 'Flest stéttarfélög styrkja námskeið af þessu tagi. Þú færð kvittun eða reikning á þínu nafni sem þú notar til að sækja um. Kannaðu rétt þinn hjá þínu félagi.'
}, {
  t: 'Vinnuveitendur',
  d: 'Fyrirtæki geta sótt um styrk úr starfsmenntasjóðum í gegnum attin.is, oft stóran hluta kostnaðar fyrir félagsfólk sjóðanna. Veldu „Fá reikning" og reikningurinn er gefinn út á kennitölu fyrirtækisins.'
}, {
  t: 'Atvinnuleitendur',
  d: 'Vinnumálastofnun styrkir oft námskeiðsgjöld atvinnuleitenda. Ráðgjafi þinn þar getur sagt þér hvað á við.'
}];
const faqs = [{
  q: 'Á hvaða tungumáli fer námskeiðið fram?',
  a: 'Á íslensku.'
}, {
  q: 'Hversu margir verða í hópnum?',
  a: `Lágmark ${CONFIG.minParticipants} og hámark ${CONFIG.maxParticipants} þátttakendur, svo allir fái tíma til að æfa sig og spyrja.`
}, {
  q: 'Hver er munurinn á hálfum og heilum degi?',
  a: 'Hálfi dagurinn leggur grunninn: rammann, líkönin og fyrstu verkfærin. Heili dagurinn er dýpri köfun, með fleiri verkfærum og meiri tíma til að setja það í framkvæmd á þínum eigin aðstæðum.'
}, {
  q: 'Þarf ég að undirbúa mig?',
  a: 'Það hjálpar að hafa í huga eitt eða tvö raunveruleg dæmi, þn eigin eða fengin að láni hjá öðrum, til að gera daginn áþreifanlegri. Þú ræður hversu mikið þú deilir.'
}, {
  q: 'Er farið með það sem ég segi sem trúnaðarmál?',
  a: 'Já. Það sem sagt er á námskeiðinu fer ekki lengra, og allar æfingar má gera með tilbúnum dæmum ef þú kýst það.'
}, {
  q: 'Get ég fengið námskeiðið styrkt?',
  a: 'Mjög líklega að hluta eða öllu leyti. Einstaklingar sækja um hjá sínu stéttarfélagi, fyrirtæki í gegnum attin.is. Sjá „Styrkir" hér að ofan.'
}, {
  q: 'Hvað ef ég þarf að afbóka?',
  a: CONFIG.cancellationPolicy
}, {
  q: 'Hvað gerist eftir námskeiðið?',
  a: 'Þú færð samantekt með helstu líkönum og aðferðum. Nokkrum vikum síðar kemur stutt eftirfylgni: hvað hefur reynst vel og hvað ekki.'
}, {
  q: 'Getum við fengið námskeiðið inn á okkar vinnustað?',
  a: 'Já. Námskeiðið er einnig í boði innanhúss og utan höfuðborgarsvæðisins, aðlagað að aðstæðum ykkar. Hafðu samband og við finnum út hvað hentar.'
}];
export function ConflictCourseSignup() {
  const {
    language
  } = useLanguage();
  const enabledOptions = CONFIG.options.filter(o => o.enabled);
  const halfOpt = enabledOptions.find(o => o.format === 'half');
  const fullOpt = enabledOptions.find(o => o.format === 'full');
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [optionId, setOptionId] = useState<string>('');
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    workplace: '',
    role: '',
    participants: '1',
    otherNames: '',
    hopes: ''
  });
  const [payment, setPayment] = useState<PaymentMethod>('card');
  const [invoice, setInvoice] = useState({
    company: '',
    kennitala: '',
    invoiceEmail: '',
    reference: ''
  });
  const [agree, setAgree] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const selected: CourseOption | undefined = enabledOptions.find(o => o.id === optionId);
  const count = Math.max(1, parseInt(form.participants || '1', 10) || 1);
  const unit = selected ? currentPrice(selected) : 0;
  const total = unit * count;
  const cardAllowed = count === 1 && !!selected && !!currentLink(selected);
  const effectivePayment: PaymentMethod = cardAllowed ? payment : 'invoice';
  const upd = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm({
    ...form,
    [k]: e.target.value
  });
  const updInv = (k: keyof typeof invoice) => (e: React.ChangeEvent<HTMLInputElement>) => setInvoice({
    ...invoice,
    [k]: e.target.value
  });
  const scrollToSignup = () => {
    document.getElementById('skraning')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  const validateStep2 = () => {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = 'Vinsamlegast skráðu nafn';
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) e.email = 'Vinsamlegast skráðu gilt netfang';
    if (count > 1 && !form.otherNames.trim()) e.otherNames = 'Vinsamlegast skráðu nöfn og netföng annarra þátttakenda';
    setErrors(e);
    return Object.keys(e).length === 0;
  };
  const validateStep3 = () => {
    const e: Record<string, string> = {};
    if (effectivePayment === 'invoice') {
      if (!invoice.company.trim()) e.company = 'Vinsamlegast skráðu heiti greiðanda';
      if (!/^\d{6}-?\d{4}$/.test(invoice.kennitala.trim())) e.kennitala = 'Kennitala á að vera 10 tölustafir';
      if (!/^\S+@\S+\.\S+$/.test(invoice.invoiceEmail.trim())) e.invoiceEmail = 'Vinsamlegast skráðu gilt netfang';
    }
    if (!agree) e.agree = 'Vinsamlegast staðfestu skilmálana';
    setErrors(e);
    return Object.keys(e).length === 0;
  };
  const submit = async () => {
    if (!selected || !validateStep3()) return;
    setSubmitting(true);
    setSubmitError('');
    const payload = {
      course: CONFIG.courseName,
      option: selected.name,
      date: selected.date,
      time: selected.time,
      unitPrice: unit,
      earlyBird: isEarly(selected) ? 'Já' : 'Nei',
      participants: count,
      total,
      payment: effectivePayment === 'card' ? 'Kort' : 'Reikningur',
      ...form,
      ...(effectivePayment === 'invoice' ? invoice : {}),
      submittedAt: new Date().toISOString(),
      _subject: `Skráning: ${selected.name}, ${selected.date} (${effectivePayment === 'card' ? 'kort' : 'reikningur'})`,
      _template: 'table'
    };
    try {
      if (CONFIG.formEndpoint) {
        const res = await fetch(CONFIG.formEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify(payload)
        });
        if (!res.ok) throw new Error('form');
      } else {
        console.info('Skráning (prófun, ekkert sent):', payload);
      }
      if (effectivePayment === 'card') {
        window.location.href = currentLink(selected);
        return;
      }
      setStep(4);
    } catch {
      setSubmitError('Eitthvað fór úrskeiðis við skráninguna. Reyndu aftur, eða sendu póst beint á helgi@helgig.com.');
    } finally {
      setSubmitting(false);
    }
  };
  const stepLabels = ['Dagsetning', 'Upplýsingar', 'Greiðsla'];
  const PriceTag = ({
    o
  }: {
    o: CourseOption;
  }) => isEarly(o) ? <span className="text-right" data-id="element-767" data-mp="1yoalzv3iifzm">
        <span className="font-bold text-coral whitespace-nowrap" data-id="element-768" data-mp="1yn969dbjx5bk">{formatISK(o.earlyPrice)}</span>
        <span className="block text-xs text-gray-500 whitespace-nowrap" data-id="element-769" data-mp="23hn920sjmeq9">
          til {shortDate(o.earlyUntil)}, svo {formatISK(o.price)}
        </span>
      </span> : <span className="font-bold text-coral whitespace-nowrap" data-id="element-770" data-mp="3j6utz7ybg75f">{formatISK(o.price)}</span>;
  return <PageTransition data-id="element-771" data-mp="1pv3l2vucj43">
      {CONFIG.draft && <div className="bg-yellow-100 border-b border-yellow-300 text-yellow-900 text-sm py-2 px-4 text-center flex items-center justify-center gap-2" data-id="element-772" data-mp="3swegcnl8mj51">
          <AlertTriangle size={16} data-id="element-773" data-mp="359fzvpx4bb74" /> DRÖG - dagsetningar, verð og greiðslutenglar eru ekki staðfest
        </div>}
      <div className="pt-12 pb-24" data-id="element-774" data-mp="3f5jcjpplis8b">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-6" data-id="element-775" data-mp="3f6kp5ym9fms5">
          <Link to="/events" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-coral" data-id="element-776" data-mp="3a6pz1xab7185">
            <ArrowLeft size={14} data-id="element-777" data-mp="ueslh4w54dwb" /> Viðburðir
          </Link>
        </div>
        {language === 'en' && <div className="max-w-3xl mx-auto px-4 mb-8" data-id="element-778" data-mp="ph0inftyj7dc">
            <Card className="p-5 bg-teal/5 border border-teal/20 text-sm text-gray-700" data-id="element-779" data-mp="pik0hav9hqh8">
              This course is taught in Icelandic. For an English-language or in-house version,{' '}
              <Link to="/contact" className="text-coral font-semibold hover:underline" data-id="element-780" data-mp="305ozrj2v8he7">get in touch</Link>.
            </Card>
          </div>}

        {/* HERO */}
        <section className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-16 text-center" data-id="element-781" data-mp="39528uan2v3ye">
          <DecorativeShape type="blob" color="bg-coral/10" className="top-0 right-0 w-[400px] h-[400px] translate-x-1/4 -translate-y-1/4 -z-10" data-id="element-782" data-mp="3e2kmuffyc9d9" />
          <DecorativeShape type="circle" color="bg-teal/10" className="bottom-0 left-0 w-64 h-64 -translate-x-1/4 translate-y-1/4 -z-10" data-id="element-783" data-mp="3nt5h5qolerqd" />
          <Animate data-id="element-784" data-mp="3rtpcqptipnae">
            <span className="inline-block px-4 py-2 bg-coral/10 text-coral font-medium rounded-full text-sm mb-6" data-id="element-785" data-mp="3ru8wmc2qalr2">
              Opið námskeið: {CONFIG.courseName}
            </span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight" data-id="element-786" data-mp="5olppah8uhy6">
              Þegar samskiptin eru orðin erfið <span className="text-coral" data-id="element-787" data-mp="ft2562qt9x1g">og hinn sér ekki vandann</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8 leading-relaxed" data-id="element-788" data-mp="armkibsjpsjl">
              Flest námskeið um ágreining ganga út frá því að báðir aðilar vilji leysa málið. Í raunveruleikanum er það sjaldnast svo einfalt. Hér lærir þú að lesa í það sem er að gerast, ná betri tökum á eigin viðbrögðum og ganga inn í krefjandi samskipti með skýrari hugsun, líka þegar hinn aðilinn sér ekki vandann.
            </p>
            <div className="flex flex-wrap justify-center gap-3 text-sm font-medium text-gray-600 mb-10" data-id="element-789" data-mp="tjno1rg9m6zc">
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100" data-id="element-790" data-mp="oq2nbrnaeywg">
                <Calendar size={16} className="text-coral" data-id="element-791" data-mp="1h8gh9g5i60q9" />
                <span data-id="element-792" data-mp="1h9rhj5zqoqcm">{enabledOptions.map(o => shortDate(o.id.slice(-10))).join(' · ')}</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100" data-id="element-793" data-mp="1cfna6lmkagb0">
                <MapPin size={16} className="text-coral" data-id="element-794" data-mp="3boosk7bza7pu" />
                <span data-id="element-795" data-mp="3bo59gnaldda8">{CONFIG.location}</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100" data-id="element-796" data-mp="2x0k93153ps60">
                <Users size={16} className="text-coral" data-id="element-797" data-mp="31xked9u4mqw2" />
                <span data-id="element-798" data-mp="2nzdpukb2i392">Hámark {CONFIG.maxParticipants} þátttakendur</span>
              </div>
            </div>
            <Button size="lg" rightIcon={<ArrowRight size={20} data-id="element-800" data-mp="2syypkwy2afmu" />} onClick={scrollToSignup} data-id="element-799" data-mp="2syqenali1kea">
              Skrá mig
            </Button>
          </Animate>
        </section>

        {/* RECOGNITION */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-801" data-mp="2iz7uou5l9ym8">
          <Animate data-id="element-802" data-mp="c9js0sqlp6ob">
            <h2 className="text-2xl font-bold text-charcoal mb-5" data-id="element-803" data-mp="c91m14e4ze37">Kannastu við þetta?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-804" data-mp="h96777pzowzr">
              {recognise.map((r, i) => <Card key={i} className="p-5 bg-white border border-gray-100 flex gap-3 items-start" data-id="element-805" data-mp="21px9p8lx29z5" data-mpi={i}>
                  <MessageCircle size={18} className="text-coral flex-shrink-0 mt-1" data-id="element-806" data-mp="1wsdkxerleesy" />
                  <p className="text-gray-700 leading-relaxed" data-id="element-807" data-mp="2bh0ae9axomxw">{r}</p>
                </Card>)}
            </div>
            <p className="text-gray-600 mt-5 leading-relaxed" data-id="element-808" data-mp="26p029tkpmnq8">
              Þetta eru aðstæðurnar sem námskeiðið snýst um. Þú byrjar á því sem þú hefur áhrif á, og það breytir oft samspilinu.
            </p>
          </Animate>
        </section>

        {/* OUTCOMES + AUDIENCE */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-809" data-mp="2ufmcnri6sk3e">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10" data-id="element-810" data-mp="2pgj43x2zzvqo">
            <Animate data-id="element-811" data-mp="2pe6p9ssje1hu">
              <h2 className="text-2xl font-bold text-charcoal mb-5" data-id="element-812" data-mp="101bnfhxojnx3">Eftir námskeiðið</h2>
              <div className="space-y-3" data-id="element-813" data-mp="10366srtaz7z2">
                {outcomes.map((o, i) => <div key={i} className="flex items-start gap-3" data-id="element-814" data-mp="v4cu31lubkzy" data-mpi={i}>
                    <div className="mt-1 p-1 rounded-full bg-coral/10 text-coral flex-shrink-0" data-id="element-815" data-mp="98b72pgi0g5q"><Check size={13} strokeWidth={3} data-id="element-816" data-mp="e7nqf085v53k" /></div>
                    <span className="text-gray-700 leading-relaxed" data-id="element-817" data-mp="e630xp0pecck">{o}</span>
                  </div>)}
              </div>
            </Animate>
            <Animate delay={0.1} data-id="element-818" data-mp="nwxnyvlrxfz2">
              <h2 className="text-2xl font-bold text-charcoal mb-5" data-id="element-819" data-mp="o01va1ujcrty">Fyrir hvern?</h2>
              <div className="space-y-3 mb-6" data-id="element-820" data-mp="3mhflgfxnyy2k">
                {audience.map((o, i) => <div key={i} className="flex items-start gap-3" data-id="element-821" data-mp="3rf7ni2s0w0kh" data-mpi={i}>
                    <div className="mt-1 p-1 rounded-full bg-teal/10 text-teal flex-shrink-0" data-id="element-822" data-mp="84wpcg3jwas"><Check size={13} strokeWidth={3} data-id="element-823" data-mp="57hjxyavr5dw" /></div>
                    <span className="text-gray-700 leading-relaxed" data-id="element-824" data-mp="592azdxzk670">{o}</span>
                  </div>)}
              </div>
              <p className="text-gray-600 leading-relaxed text-sm border-l-4 border-teal/30 pl-4" data-id="element-825" data-mp="1gmmqxei1nq3b">
                Námskeiðið er verklegt og gagnvirkt: stuttar innlagnir, mikið af æfingum og samtölum í litlum hópum. Þú vinnur með raunverulegar aðstæður, en deilir aðeins því sem þú kýst að deila.
              </p>
            </Animate>
          </div>
        </section>

        {/* OPTIONS + AGENDA */}
        <section className="bg-gray-50 py-16 mb-20" data-id="element-826" data-mp="2mg2ed3ikz0xu">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-827" data-mp="2mf9e8le4n9oo">
            <Animate className="mb-8" data-id="element-828" data-mp="2hgy5kdzkj581">
              <h2 className="text-2xl font-bold text-charcoal" data-id="element-829" data-mp="2hi0x8o4ptbp0">Hálfur dagur eða heill dagur</h2>
              <p className="text-gray-500 mt-1" data-id="element-830" data-mp="2ddt3udnbb7kg">Hálfi dagurinn leggur grunninn. Heili dagurinn er dýpri köfun, með fleiri verkfærum og meiri tíma til að setja það í framkvæmd.</p>
            </Animate>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-831" data-mp="23irepht1e5hv">
              {halfOpt && <Animate data-id="element-832" data-mp="6nvb6ona48gu">
                  <Card className="p-7 h-full border-2 border-gray-100 bg-white" data-id="element-833" data-mp="1pk24g4t61zo">
                    <div className="flex items-start justify-between gap-4 mb-2" data-id="element-834" data-mp="1nh0eyuamkf4">
                      <h3 className="font-bold text-xl text-charcoal" data-id="element-835" data-mp="tiuxy6sbs7nm">Hálfur dagur: grunnurinn</h3>
                      <PriceTag o={halfOpt} data-id="element-836" data-mp="olbkva7d60x9" />
                    </div>
                    <p className="text-sm text-gray-500 mb-5" data-id="element-837" data-mp="jsrvzh387jd5">
                      9:00 - 12:30. {enabledOptions.filter(o => o.format === 'half').map(o => shortDate(o.id.slice(-10))).join(' eða ')}. Kaffi og léttar veitingar innifalin.
                    </p>
                    <div className="space-y-2" data-id="element-838" data-mp="5u21m97sjxsh">
                      {halfDayAgenda.map((a, i) => <div key={i} className="flex gap-3 text-sm" data-id="element-839" data-mp="upavddsu6km" data-mpi={i}>
                          <span className="w-12 flex-shrink-0 font-medium text-gray-400" data-id="element-840" data-mp="x1stqjk4gxo">{a.t}</span>
                          <span className={a.d === 'Hlé' ? 'text-gray-400 italic' : 'text-gray-700'} data-id="element-841" data-mp="3s3d1a7qwcjz1">{a.d}</span>
                        </div>)}
                    </div>
                  </Card>
                </Animate>}
              {fullOpt && <Animate delay={0.08} data-id="element-842" data-mp="1yqnr5fbiduye">
                  <Card className="p-7 h-full border-2 border-coral/30 bg-white" data-id="element-843" data-mp="22s9czq60tjkq">
                    <div className="flex items-start justify-between gap-4 mb-2" data-id="element-844" data-mp="22q50iju23c8u">
                      <h3 className="font-bold text-xl text-charcoal" data-id="element-845" data-mp="27lat21hyrp5w">Heill dagur: dýpri köfun</h3>
                      <PriceTag o={fullOpt} data-id="element-846" data-mp="2ckf12ot5jgun" />
                    </div>
                    <p className="text-sm text-gray-500 mb-5" data-id="element-847" data-mp="1ftd36qvbuny9">
                      9:00 - 16:00. {shortDate(fullOpt.id.slice(-10))}. Hádegismatur, kaffi og léttar veitingar innifalin.
                    </p>
                    <div className="space-y-2" data-id="element-848" data-mp="1or5ap8ut4rb3">
                      {fullDayAgenda.map((a, i) => <div key={i} className="flex gap-3 text-sm" data-id="element-849" data-mp="1tt4dohmmxm7i" data-mpi={i}>
                          <span className="w-12 flex-shrink-0 font-medium text-gray-400" data-id="element-850" data-mp="306twhwwbrkab">{a.t}</span>
                          <span className={a.d === 'Hlé' || a.d === 'Hádegismatur' ? 'text-gray-400 italic' : 'text-gray-700'} data-id="element-851" data-mp="355x33lrydyke">{a.d}</span>
                        </div>)}
                    </div>
                  </Card>
                </Animate>}
            </div>
          </div>
        </section>

        {/* TRAINER */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-852" data-mp="2penvosvsx2an">
          <Animate data-id="element-853" data-mp="2kip1gonskq2b">
            <h2 className="text-2xl font-bold text-charcoal mb-5" data-id="element-854" data-mp="2kg2twp0wiak3">Leiðbeinandi</h2>
            <p className="text-gray-600 leading-relaxed mb-4" data-id="element-855" data-mp="1wq8m4lvfvc6p">
              Helgi Guðmundsson er menntaður í vinnu- og skipulagssálfræði frá Gautaborgarháskóla, með sérþjálfun í lausn ágreininga og sáttamiðlun og hefur lokið ICF-viðurkenndu markþjálfanámi. Hann hefur þjálfað stjórnendur og teymi á alþjóðavísu, meðal annars hjá Egencia (Expedia Group), kennt við IHM Business School í Gautaborg og er meðhöfundur tveggja kafla í bókinni <em data-id="element-856" data-mp="192s6aipd19wn">Agile People Principles</em>.
            </p>
            <p className="text-gray-600 leading-relaxed" data-id="element-857" data-mp="192ic0ixa8gl5">
              Hann hefur skrifað ítarlega um ágreining, endurgjöf og samskipti á vinnustöðum,{' '}
              <Link to="/writing/on-conflict" className="text-coral font-semibold hover:underline" data-id="element-858" data-mp="3ph3vmirrpbxx">sjá greinaröðina On Conflict</Link>.
            </p>
          </Animate>
        </section>

        {/* FUNDING */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-16" data-id="element-859" data-mp="3fs2h6x0vf2m2">
          <Animate data-id="element-860" data-mp="7apiun58j814">
            <h2 className="text-2xl font-bold text-charcoal mb-2" data-id="element-861" data-mp="7c8z4dqw5j2w">Styrkir</h2>
            <p className="text-gray-600 mb-6" data-id="element-862" data-mp="cau0whlskatg">Fæstir greiða námskeið af þessu tagi að fullu sjálfir. Þú gætir fengið það styrkt að hluta eða öllu leyti.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-863" data-mp="3ufnfmdmswdnn">
              {funding.map((f, i) => <Card key={i} className="p-5 bg-white border border-gray-100" data-id="element-864" data-mp="2e7e3n9wn9os" data-mpi={i}>
                  <h3 className="font-bold text-charcoal mb-2" data-id="element-865" data-mp="2izgq67wak46h">{f.t}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed" data-id="element-866" data-mp="2n3eoigdslayh">{f.d}</p>
                </Card>)}
            </div>
          </Animate>
        </section>

        {/* SIGNUP */}
        <section id="skraning" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mb-20 scroll-mt-24" data-id="element-867" data-mp="2yhqg00i1hhgq">
          <Animate data-id="element-868" data-mp="2tkq7zk1mq4mz">
            <Card className="p-8 md:p-10 border-t-4 border-coral" data-id="element-869" data-mp="2tk81zvp60c1v">
              <h2 className="text-3xl font-bold text-charcoal mb-2" data-id="element-870" data-mp="2oldckawozp8s">Skráning</h2>
              {step < 4 && <div className="flex items-center gap-2 mb-8 text-sm" data-id="element-871" data-mp="3m7ax0dumu4nd">
                  {stepLabels.map((l, i) => <React.Fragment key={l} data-id="element-872" data-mp="3h9sryyjqk9kn" data-mpi={i}>
                      <div className={`flex items-center gap-2 ${step >= i + 1 ? 'text-coral font-bold' : 'text-gray-400'}`} data-id="element-873" data-mp="3c9wkgpbty00c">
                        <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs ${step > i + 1 ? 'bg-coral text-white' : step === i + 1 ? 'border-2 border-coral' : 'border-2 border-gray-200'}`} data-id="element-874" data-mp="387jga5cqea5o">
                          {step > i + 1 ? <Check size={14} data-id="element-875" data-mp="1wvimud4x9xjl" /> : i + 1}
                        </span>
                        <span className="hidden sm:inline" data-id="element-876" data-mp="ccydavugzybo">{l}</span>
                      </div>
                      {i < 2 && <div className="flex-1 h-0.5 bg-gray-200" data-id="element-877" data-mp="gd8ii6afukph" />}
                    </React.Fragment>)}
                </div>}

              {/* STEP 1 */}
              {step === 1 && <div data-id="element-878" data-mp="q4mpt08msot6">
                  <p className="font-medium text-charcoal mb-4" data-id="element-879" data-mp="v2ee7ka2hssm">Veldu dagsetningu</p>
                  <div className="space-y-3 mb-8" data-id="element-880" data-mp="100y0xmwhj8m1">
                    {enabledOptions.map(o => <label key={o.id} className={`flex items-start gap-4 p-4 rounded-2xl border-2 cursor-pointer transition-colors ${optionId === o.id ? 'border-coral bg-coral/5' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-881" data-mp="142t1gp6ldhyr">
                        <input type="radio" name="option" className="mt-1 accent-coral" checked={optionId === o.id} onChange={() => setOptionId(o.id)} data-id="element-882" data-mp="35upkejv39u3k" />
                        <div className="flex-1" data-id="element-883" data-mp="3auu5yoautf0n">
                          <div className="flex items-start justify-between gap-4" data-id="element-884" data-mp="hzk5k98gfqgj">
                            <span className="font-bold text-charcoal" data-id="element-885" data-mp="czzbac48c6a5">{o.name}</span>
                            <PriceTag o={o} data-id="element-886" data-mp="qyyydfc3gd47" />
                          </div>
                          <div className="flex flex-wrap gap-4 text-sm text-gray-500 mt-1" data-id="element-887" data-mp="qwujk2cnptp5">
                            <span className="flex items-center gap-1" data-id="element-888" data-mp="m28iot47bjjb"><Calendar size={14} data-id="element-889" data-mp="10wv8c3tap20q" />{o.date}</span>
                            <span className="flex items-center gap-1" data-id="element-890" data-mp="10v1yygras3p1"><Clock size={14} data-id="element-891" data-mp="vz3dp219n1su" />{o.time}</span>
                          </div>
                        </div>
                      </label>)}
                  </div>
                  <div className="flex justify-end" data-id="element-892" data-mp="15nv3b3iuta0l">
                    <Button disabled={!optionId} rightIcon={<ArrowRight size={18} data-id="element-894" data-mp="1jncs3iw5gn9y" />} onClick={() => setStep(2)} data-id="element-893" data-mp="15q7qpw7dscl1">Áfram</Button>
                  </div>
                </div>}

              {/* STEP 2 */}
              {step === 2 && <div data-id="element-895" data-mp="1yabh777jb5li">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5" data-id="element-896" data-mp="1yb4k1x3dhayd">
                    <Input label="Nafn *" name="name" value={form.name} onChange={upd('name')} error={errors.name} data-id="element-897" data-mp="238mw3wcos6al" />
                    <Input label="Netfang *" name="email" type="email" value={form.email} onChange={upd('email')} error={errors.email} data-id="element-898" data-mp="1obvrn8o5o4e4" />
                    <Input label="Sími" name="phone" type="tel" value={form.phone} onChange={upd('phone')} data-id="element-899" data-mp="1fk3m9cdznlge" />
                    <Input label="Vinnustaður" name="workplace" value={form.workplace} onChange={upd('workplace')} data-id="element-900" data-mp="1kajzavdwd528" />
                    <Input label="Starfsheiti" name="role" value={form.role} onChange={upd('role')} data-id="element-901" data-mp="15mfnmbd9htsy" />
                    <Input label="Fjöldi þátttakenda" name="participants" type="number" min={1} max={CONFIG.maxParticipants} value={form.participants} onChange={upd('participants')} data-id="element-902" data-mp="1ak7hvc1dps0b" />
                  </div>
                  {count > 1 && <div className="mb-5" data-id="element-903" data-mp="10m1fktbnujux">
                      <TextArea label="Nöfn og netföng annarra þátttakenda *" name="otherNames" rows={3} value={form.otherNames} onChange={upd('otherNames')} error={errors.otherNames} data-id="element-904" data-mp="1h2vi8b7l2vaf" />
                    </div>}
                  <div className="mb-8" data-id="element-905" data-mp="172uvefai9x1h">
                    <TextArea label="Hvað vonast þú til að fá út úr námskeiðinu? (valfrjálst)" name="hopes" rows={3} value={form.hopes} onChange={upd('hopes')} data-id="element-906" data-mp="175z8k25y834a" />
                  </div>
                  <div className="flex justify-between" data-id="element-907" data-mp="y5kjqsts05w1">
                    <Button variant="ghost" leftIcon={<ArrowLeft size={18} data-id="element-909" data-mp="tbgag2x1flrs" />} onClick={() => setStep(1)} data-id="element-908" data-mp="tchq6kp00wfu">Til baka</Button>
                    <Button rightIcon={<ArrowRight size={18} data-id="element-911" data-mp="obk7m702u0dt" />} onClick={() => validateStep2() && setStep(3)} data-id="element-910" data-mp="oe6cfyvl21sc">Áfram</Button>
                  </div>
                </div>}

              {/* STEP 3 */}
              {step === 3 && selected && <div data-id="element-912" data-mp="addhjd1dd4op">
                  <div className="bg-gray-50 rounded-2xl p-5 mb-6 text-sm" data-id="element-913" data-mp="aeych3rlipn3">
                    <div className="flex justify-between mb-1" data-id="element-914" data-mp="2m1s4cpr6v985"><span className="text-gray-500" data-id="element-915" data-mp="2m0pfsohc9ive">Námskeið</span><span className="font-medium text-charcoal text-right" data-id="element-916" data-mp="2q2kedl7vxi5h">{selected.name}</span></div>
                    <div className="flex justify-between mb-1" data-id="element-917" data-mp="2v26sdwvjf2fc"><span className="text-gray-500" data-id="element-918" data-mp="2v2ghzjco77ki">Tími</span><span className="text-charcoal text-right" data-id="element-919" data-mp="30102rgfj2dba">{selected.date}, {selected.time}</span></div>
                    <div className="flex justify-between mb-1" data-id="element-920" data-mp="22djcyhxgz79p"><span className="text-gray-500" data-id="element-921" data-mp="22ekkicfa1bmo">Þátttakendur</span><span className="text-charcoal" data-id="element-922" data-mp="27cch3jqv9lx7">{count} x {formatISK(unit)}{isEarly(selected) ? ' (snemmskráning)' : ''}</span></div>
                    <div className="flex justify-between pt-3 mt-3 border-t border-gray-200 text-base" data-id="element-923" data-mp="2cae6emevyhh4"><span className="font-bold text-charcoal" data-id="element-924" data-mp="2h2nqvmx8epc0">Samtals</span><span className="font-bold text-coral" data-id="element-925" data-mp="2h48qhqyekygq">{formatISK(total)}</span></div>
                  </div>

                  <p className="font-medium text-charcoal mb-3" data-id="element-926" data-mp="1olv0gdjb6gs7">Greiðslumáti</p>
                  <div className={`grid grid-cols-1 ${cardAllowed ? 'sm:grid-cols-2' : ''} gap-3 mb-6`} data-id="element-927" data-mp="1ojrzwzkj5598">
                    {cardAllowed && <label className={`flex items-center gap-3 p-4 rounded-2xl border-2 cursor-pointer transition-colors ${effectivePayment === 'card' ? 'border-coral bg-coral/5' : 'border-gray-200'}`} data-id="element-928" data-mp="13bk33nzuodgf">
                        <input type="radio" name="payment" className="accent-coral" checked={effectivePayment === 'card'} onChange={() => setPayment('card')} data-id="element-929" data-mp="tc30xgjt4hp7" />
                        <CreditCard size={20} className="text-coral" data-id="element-930" data-mp="fjmtm3jvhxih" />
                        <span className="font-medium text-charcoal" data-id="element-931" data-mp="kdr5n17zwvqf">Greiða með korti</span>
                      </label>}
                    <label className={`flex items-center gap-3 p-4 rounded-2xl border-2 cursor-pointer transition-colors ${effectivePayment === 'invoice' ? 'border-coral bg-coral/5' : 'border-gray-200'}`} data-id="element-932" data-mp="am3hb96qjut6">
                      <input type="radio" name="payment" className="accent-coral" checked={effectivePayment === 'invoice'} onChange={() => setPayment('invoice')} data-id="element-933" data-mp="1leze3e81ab0" />
                      <FileText size={20} className="text-coral" data-id="element-934" data-mp="3e7dezzrm9o3q" />
                      <span className="font-medium text-charcoal" data-id="element-935" data-mp="397hlwvkgjh2y">Fá reikning</span>
                    </label>
                  </div>
                  {count > 1 && <p className="text-sm text-gray-500 -mt-3 mb-6" data-id="element-936" data-mp="3j3uf7sc3bsx6">Fyrir fleiri en einn þátttakanda er greitt gegn reikningi.</p>}

                  {effectivePayment === 'card' && <p className="text-sm text-gray-500 mb-6" data-id="element-937" data-mp="3s5k68x228puf">Þegar þú staðfestir ferðu á örugga greiðslusíðu. Kvittun berst í tölvupósti og nýtist til að sækja um styrk hjá stéttarfélagi.</p>}

                  {effectivePayment === 'invoice' && <>
                      <p className="text-sm text-gray-500 mb-4" data-id="element-938" data-mp="jopbk4vf2brf">Reikningur er gefinn út á kennitölu greiðanda. Ef vinnuveitandi greiðir getur hann sótt um styrk í gegnum attin.is.</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6" data-id="element-939" data-mp="38i41opxwqclm">
                        <Input label="Greiðandi (fyrirtæki eða einstaklingur) *" name="company" value={invoice.company} onChange={updInv('company')} error={errors.company} data-id="element-940" data-mp="38gaqcxccn47a" />
                        <Input label="Kennitala greiðanda *" name="kennitala" value={invoice.kennitala} onChange={updInv('kennitala')} error={errors.kennitala} placeholder="000000-0000" data-id="element-941" data-mp="3hh8zfu1na1wq" />
                        <Input label="Netfang fyrir reikning *" name="invoiceEmail" type="email" value={invoice.invoiceEmail} onChange={updInv('invoiceEmail')} error={errors.invoiceEmail} data-id="element-942" data-mp="3r7ttr5aack9u" />
                        <Input label="Tilvísun / pöntunarnúmer" name="reference" value={invoice.reference} onChange={updInv('reference')} data-id="element-943" data-mp="52gfjz18d1qc" />
                      </div>
                    </>}

                  <label className="flex items-start gap-3 text-sm text-gray-600 mb-2 cursor-pointer" data-id="element-944" data-mp="26u37c2sy50nv">
                    <input type="checkbox" className="mt-1 accent-coral" checked={agree} onChange={e => setAgree(e.target.checked)} data-id="element-945" data-mp="2auvjb3t7djnu" />
                    <span data-id="element-946" data-mp="23cowj6azl18w">Ég hef kynnt mér <Link to="/terms" className="text-coral hover:underline" data-id="element-947" data-mp="1yjm3r0e19vtr">skilmála</Link> og afbókunarreglur: {CONFIG.cancellationPolicy}</span>
                  </label>
                  {errors.agree && <p className="text-sm text-red-500 mb-4" data-id="element-948" data-mp="1pjpkxfebrr6m">{errors.agree}</p>}
                  {submitError && <p className="text-sm text-red-500 mt-4" data-id="element-949" data-mp="2m9q48yvxdfgj">{submitError}</p>}

                  <div className="flex justify-between mt-8" data-id="element-950" data-mp="2m7m32opeuujy">
                    <Button variant="ghost" leftIcon={<ArrowLeft size={18} data-id="element-952" data-mp="2h8iy149fkobz" />} onClick={() => setStep(2)} data-id="element-951" data-mp="2hbolkf2evi5l">Til baka</Button>
                    <Button size="lg" isLoading={submitting} rightIcon={effectivePayment === 'card' ? <CreditCard size={18} data-id="element-954" data-mp="2895o6ahe9xqq" /> : <Check size={18} data-id="element-955" data-mp="28c1m3yt8smf6" />} onClick={submit} data-id="element-953" data-mp="2d776gt6zxt15">
                      {effectivePayment === 'card' ? 'Staðfesta og greiða' : 'Staðfesta skráningu'}
                    </Button>
                  </div>
                </div>}

              {/* STEP 4 - confirmation (invoice) */}
              {step === 4 && selected && <div className="text-center py-6" data-id="element-956" data-mp="2pigbyvvqmkb6">
                  <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-teal/10 text-teal flex items-center justify-center" data-id="element-957" data-mp="2pk9jede6d8k8"><Check size={32} strokeWidth={3} data-id="element-958" data-mp="2boysfb31x7l5" /></div>
                  <h3 className="text-2xl font-bold text-charcoal mb-3" data-id="element-959" data-mp="2ft6jvg0w91n2">Takk fyrir skráninguna</h3>
                  <p className="text-gray-600 max-w-md mx-auto leading-relaxed" data-id="element-960" data-mp="33j997odfbthz">
                    Skráning þín er móttekin: {selected.name}, {selected.date}. Reikningur verður sendur á {invoice.invoiceEmail} og þú færð staðfestingu og nánari upplýsingar í tölvupósti.
                  </p>
                </div>}
            </Card>
          </Animate>
        </section>

        {/* FAQ */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-961" data-mp="1j1bdabkjv95w">
          <Animate data-id="element-962" data-mp="1o1h7nzi7f9ro">
            <h2 className="text-2xl font-bold text-charcoal mb-6" data-id="element-963" data-mp="1o1yzmjxu31ts">Algengar spurningar</h2>
            <div className="space-y-3" data-id="element-964" data-mp="1fj5iz2m9p2h0">
              {faqs.map((f, i) => <div key={i} className="border-2 border-gray-100 rounded-2xl bg-white" data-id="element-965" data-mp="1bj4u0o1jl8vp" data-mpi={i}>
                  <button className="w-full flex items-center justify-between gap-4 p-5 text-left font-bold text-charcoal" onClick={() => setOpenFaq(openFaq === i ? null : i)} data-id="element-966" data-mp="1pi4fjmtrd7nn">
                    {f.q}
                    <ChevronDown size={18} className={`flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} data-id="element-967" data-mp="wt5x0olq43ht" />
                  </button>
                  {openFaq === i && <p className="px-5 pb-5 text-gray-600 leading-relaxed" data-id="element-968" data-mp="rsrnt38dyni7">{f.a}</p>}
                </div>)}
            </div>
          </Animate>
        </section>

        {/* IN-HOUSE CTA */}
        <section className="max-w-4xl mx-auto px-4 text-center" data-id="element-969" data-mp="e37q4btnwgfr">
          <Animate data-id="element-970" data-mp="e1mvkm7ckxhw">
            <Card className="p-12 bg-gradient-to-br from-coral/5 to-orange-50 border-0" data-id="element-971" data-mp="e14sp6q6jl10">
              <h2 className="text-2xl font-bold mb-3" data-id="element-972" data-mp="920hybnlxf8k">Viltu frekar fá námskeiðið til ykkar?</h2>
              <p className="text-gray-600 mb-8 max-w-xl mx-auto" data-id="element-973" data-mp="29644oqbnu3ki">Námskeiðið er einnig í boði innanhúss, fyrir stjórnendahópa og teymi, og á landsbyggðinni. Aðlagað að aðstæðum ykkar.</p>
              <Link to="/contact" data-id="element-974" data-mp="2iwp94ue0m2bc"><Button variant="secondary" rightIcon={<ArrowRight size={18} data-id="element-976" data-mp="2myaja7z602hu" />} data-id="element-975" data-mp="2iya42l48rn9q">Hafa samband</Button></Link>
            </Card>
          </Animate>
        </section>
      </div>
    </PageTransition>;
}